
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import {
  Category, Lead, OperationInfo, Tag,
} from '@/types';

@Component
export default class LeadCategoriesAndTags extends Vue {
  @Prop(Object) readonly lead: undefined | Lead;
  @Prop(Object) readonly operationInfo: OperationInfo | undefined;
  @Prop({ type: Boolean, default: false }) readonly small!: boolean;
  @Prop(String) readonly pageSource: string | undefined;

  get categories(): null | Category[] {
    return this.$store.getters.categories;
  }

  get tags(): null | Tag[] {
    return this.$store.getters.tags;
  }

  get hasTags(): boolean {
    if (this.lead !== undefined) {
      return typeof this.lead.flatTags === 'string' && this.lead.flatTags.length > 2;
    }

    if (this.operationInfo !== undefined) {
      return typeof this.operationInfo.tags === 'string' && this.operationInfo.tags.length > 2;
    }

    return false;
  }

  get hasCategories(): boolean {
    if (this.lead !== undefined) {
      return typeof this.lead.flatCategories === 'string' && this.lead.flatCategories.length > 2;
    }

    if (this.operationInfo !== undefined) {
      return typeof this.operationInfo.categories === 'string' && this.operationInfo.categories.length > 2;
    }

    return false;
  }

  get leadCategories(): number[] {
    if (this.lead !== undefined) {
      return this.hasCategories && 'flatCategories' in this.lead && this.lead.flatCategories
        ? this.lead.flatCategories.substring(1, this.lead.flatCategories.length - 1).split(':')
          .map((s: string) => Number(s))
          .filter((n: number) => this.checkCategoryVisibility(n))
        : [];
    }

    if (this.operationInfo !== undefined) {
      return this.hasCategories && 'categories' in this.operationInfo && this.operationInfo.categories
        ? this.operationInfo?.categories.substring(1, this.operationInfo.categories.length - 1).split(':')
          .map((s: string) => Number(s))
          .filter((n: number) => this.checkCategoryVisibility(n))
        : [];
    }

    return [];
  }

  get leadTags(): number[] {
    if (this.lead !== undefined) {
      return this.hasTags && 'flatTags' in this.lead && this.lead.flatTags
        ? this.lead.flatTags.substring(1, this.lead.flatTags.length - 1).split(':')
          .map((s: string) => Number(s))
          .filter((n: number) => this.checkTagVisibility(n))
        : [];
    }

    if (this.operationInfo !== undefined) {
      return this.hasTags && 'tags' in this.operationInfo && this.operationInfo.tags
        ? this.operationInfo.tags.substring(1, this.operationInfo.tags.length - 1).split(':')
          .map((s: string) => Number(s))
          .filter((n: number) => this.checkTagVisibility(n))
        : [];
    }

    return [];
  }

  leadCategory(categoryId: number) {
    if (!this.categories) return null;

    return this.categories.find((category) => category.id === categoryId);
  }

  categoryColor(categoryId: number): string {
    const category = this.leadCategory(categoryId);

    const baseColor = category?.color ? category.color : 'blue-grey';

    // Se la categoria è nascosta o disattivata, schiarisce il colore
    if (category?.hidden || category?.isActive === false) return `${baseColor} lighten-3`;

    return baseColor;
  }

  checkCategoryVisibility(categoryId: number) {
    const category = this.leadCategory(categoryId);

    if (this.pageSource === 'operations') return category?.showInOperationList;

    return category?.showInConnectionList;
  }

  leadTag(tagId: number) {
    if (!this.tags) return null;

    return this.tags.find((tag) => tag.id === tagId);
  }

  tagColor(tagId: number): string {
    const tag = this.leadTag(tagId);

    const baseColor = tag?.color ? tag.color : 'light-blue';

    // Se il tag è nascosto o disattivato, schiarisce il colore
    if (tag?.hidden || tag?.isActive === false) return `${baseColor} lighten-3`;

    return baseColor;
  }

  checkTagVisibility(tagId: number) {
    const tag = this.leadTag(tagId);

    if (this.pageSource === 'operations') return tag?.showInOperationList;

    return tag?.showInConnectionList;
  }

  onClick() {
    this.$emit('click');
  }
}
